import { useState, useEffect } from "react";
import { getPaymentsByCompany, formatCurrency } from "../utils/cmsApi";
import { getPaymentTypeLabel } from "../utils/paymentTypes";
import { useSubscription } from "../context/SubscriptionContext";

const CATEGORY_COLORS = [
  { color: "#f59e0b", light: "rgba(245,158,11,0.15)" },  // gold
  { color: "#6366f1", light: "rgba(99,102,241,0.15)" },  // purple
  { color: "#14b8a6", light: "rgba(20,184,166,0.15)" },  // teal
  { color: "#f43f5e", light: "rgba(244,63,94,0.15)" },   // red
  { color: "#3b82f6", light: "rgba(59,130,246,0.15)" },  // blue
  { color: "#a855f7", light: "rgba(168,85,247,0.15)" },  // violet
  { color: "#22c55e", light: "rgba(34,197,94,0.15)" },   // green
  { color: "#f97316", light: "rgba(249,115,22,0.15)" },  // orange
];

function buildDonutPath(cx, cy, r, startAngle, endAngle) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const x1 = cx + r * Math.cos(toRad(startAngle));
  const y1 = cy + r * Math.sin(toRad(startAngle));
  const x2 = cx + r * Math.cos(toRad(endAngle));
  const y2 = cy + r * Math.sin(toRad(endAngle));
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
}

export default function PaymentDonut({ npi, year }) {
  const { isPro, setShowUpgradeModal, setUpgradeMessage } = useSubscription();
  const isPaidTier = isPro;
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  useEffect(() => {
    setLoading(true);
    getPaymentsByCompany(npi, year)
      .then((companies) => {
        // Aggregate all categories across all companies
        const catMap = {};
        const catCountMap = {};
        for (const company of companies) {
          for (const [cat, amount] of Object.entries(company.categories)) {
            catMap[cat] = (catMap[cat] || 0) + amount;
          }
          // Count payments per category from payment records
          for (const payment of company.payments || []) {
            const cat = payment.natureOfPayment || "Other";
            catCountMap[cat] = (catCountMap[cat] || 0) + 1;
          }
        }
        const sorted = Object.entries(catMap)
          .sort((a, b) => b[1] - a[1])
          .map(([name, amount], i) => ({
            name,
            amount,
            count: catCountMap[name] || 0,
            color: CATEGORY_COLORS[i % CATEGORY_COLORS.length].color,
            light: CATEGORY_COLORS[i % CATEGORY_COLORS.length].light,
          }));                    
        setCategories(sorted);
      })
      .catch(() => setCategories([]))
      .finally(() => setLoading(false));
  }, [npi, year]);

  if (loading) {
    return (
      <div className="card mb-6 animate-pulse">
        <div className="h-4 bg-white/10 rounded w-1/3 mb-4" />
        <div className="flex gap-6 items-center">
          <div className="w-32 h-32 rounded-full bg-white/5 shrink-0" />
          <div className="space-y-2 flex-1">
            {[1,2,3,4].map(i => <div key={i} className="h-3 bg-white/5 rounded w-3/4" />)}
          </div>
        </div>
      </div>
    );
  }

  if (categories.length === 0) return null;

  const total = categories.reduce((s, c) => s + c.amount, 0);

  function exportDonutCsv() {
    const rows = [["Payment Category", "Amount ($)", "% of Total", "Payment Count", "Avg Payment ($)"]];
    for (const cat of categories) {
      rows.push([
        cat.name,
        cat.amount.toFixed(2),
        `${(cat.amount / total * 100).toFixed(1)}%`,
        cat.count,
        cat.count > 0 ? (cat.amount / cat.count).toFixed(2) : "0.00",
      ]);
    }
    const csv = rows.map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `payment_breakdown_${year}.csv`;
    a.click();
  }

  // Build donut segments
  const cx = 60;
  const cy = 60;
  const r = 45;
  const innerR = 28;
  const gap = 2;

  let currentAngle = -90;
  const segments = categories.map((cat) => {
    const pct = cat.amount / total;
    const angle = pct * 360;
    const start = currentAngle + gap / 2;
    const end = currentAngle + angle - gap / 2;
    currentAngle += angle;
    return { ...cat, pct, startAngle: start, endAngle: end };
  });

  const hovered = hoveredIndex !== null ? categories[hoveredIndex] : null;

  return (
    <div className="card mb-6 overflow-hidden">
      <h2 className="text-white/40 text-xs uppercase tracking-widest mb-4">{year} Payment breakdown</h2>
      <div className="flex gap-4 items-start">        

        {/* Donut SVG */}
        <div className="shrink-0 relative" style={{ width: 120, height: 120 }}>
          <svg width="120" height="120" viewBox="0 0 120 120">
            {segments.map((seg, i) => (
              <path
                key={i}
                d={buildDonutPath(cx, cy, r, seg.startAngle, seg.endAngle)}
                stroke={seg.color}
                strokeWidth={hoveredIndex === i ? 20 : 16}
                fill="none"
                strokeLinecap="butt"
                opacity={hoveredIndex === null || hoveredIndex === i ? 1 : 0.3}
                style={{ cursor: "pointer", transition: "all 0.15s" }}
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(null)}
              />
            ))}
            {/* Center text */}
            <text x={cx} y={cy - 6} textAnchor="middle" fill="white" fontSize="9" opacity="0.5">
              {hovered ? getPaymentTypeLabel(hovered.name).split(" ")[0] : "Total"}
            </text>            
            <text x={cx} y={cy + 8} textAnchor="middle" fill="white" fontSize="10" fontWeight="500">
              {hovered
                ? (hovered.pct && !isNaN(hovered.pct)) ? `${(hovered.pct * 100).toFixed(1)}%` : "0%"
                : formatCurrency(total).replace("$", "$")}
            </text>            
          </svg>
        </div>

        {/* Legend */}
        <div className="flex-1 space-y-2">
          {categories.map((cat, i) => (
            <div
              key={i}
              className="cursor-pointer rounded px-2 py-1 transition-all"
              style={{ background: hoveredIndex === i ? cat.light : "transparent" }}
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <div className="flex items-baseline justify-between gap-2">
                <div className="flex items-start gap-2 min-w-0 flex-1">
                  <div className="w-2 h-2 rounded-full shrink-0 mt-1" style={{ background: cat.color }} />
                  <span className="text-white/60 text-xs leading-relaxed break-words">{getPaymentTypeLabel(cat.name)}</span>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-white/80 text-xs font-mono whitespace-nowrap">{formatCurrency(cat.amount)}</div>
                  <div className="text-white/30 text-xs">{(cat.amount / total * 100).toFixed(1)}%</div>
                </div>
              </div>
              {cat.count > 0 && (
                <div className="text-right text-white/25 text-xs mt-0.5">
                  {cat.count} payment{cat.count !== 1 ? "s" : ""} · avg {formatCurrency(cat.amount / cat.count)}
                </div>
              )}              
            </div>            
          ))}
        </div>        
      </div>
      <div className="print:hidden mt-3 flex items-center justify-between">
        <button
          onClick={() => {
            if (!isPaidTier) {
              setUpgradeMessage("Exporting chart data is a Pro feature. Upgrade to download payment breakdown as a CSV.");
              setShowUpgradeModal(true);
              return;
            }
            exportDonutCsv();
          }}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all"
        >
          {isPaidTier ? "⬇ Export to CSV" : "🔒 Export to CSV"}
        </button>
      </div>
    </div>
  );
}